/**
 * frontend/src/crm/pages/CompanyDetail.jsx — fiche d'entreprise redesignée
 * Branche cible : v4. Reprend le standard visuel de la page Téléphonie :
 * fond #f6f7f9, cartes blanches border-slate-200 rounded-2xl, accent indigo,
 * onglets pilule sombres (slate-900 actif), valeurs chiffrées en font-mono.
 *
 * L'API, les routes et les toasts sont inchangés par rapport à l'existant :
 * seuls le balisage et les classes ont été revus.
 */
import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/crmApi";
import { Loader } from "@/crm/ui";
import { useAuth } from "@/context/CrmAuth";
import { toast } from "sonner";
import { ExclusionBanner, ExclusionButton } from "@/crm/components/ExclusionControls";
import { ClientBanner, ClientButton } from "@/crm/components/ClientControls";
import CallCampaignBanner from "@/crm/components/CallCampaignBanner";
import CallCampaignForm from "@/crm/components/CallCampaignForm";
import { useTwilio } from "@/context/TwilioVoice";
import SiteCard from "@/crm/components/SiteCard";
import CompanyNotes from "@/crm/components/CompanyNotes";
import PrepareCallButton from "@/crm/components/copilot/PrepareCallButton";
import CompanyConversations from "@/crm/components/copilot/CompanyConversations";
import DirigeantNetwork from "@/crm/components/DirigeantNetwork";
import AddressAutocomplete from "@/crm/components/AddressAutocomplete";
import PlanActionModal from "@/crm/components/PlanActionModal";
import CompanySolution from "@/crm/components/solutions/CompanySolution";
import PurgeCompanyModal from "@/crm/components/debug/PurgeCompanyModal";
import TrashCompanyModal from "@/crm/components/TrashCompanyModal";
import AiEnrichPanel from "@/crm/components/ai/AiEnrichPanel";
import useDebugMode from "@/crm/components/debug/useDebugMode";
import SystemActivities from "@/crm/components/debug/SystemActivities";
import { actionLabel } from "@/crm/actions";
import {
  ArrowLeft, Phone, Mail, Globe, Building2, ExternalLink, Sparkles, PhoneCall, Plus,
  UserPlus, Clock, MessageSquare, History, Users, CheckSquare, X, Trash2,
  CalendarDays, Briefcase, FileText, Hash, ChevronDown, Store, Receipt, Layers,
  UserCog, Award, TrendingUp, Network, Landmark, Pencil, MessagesSquare, CalendarPlus,
  BadgeCheck, PhoneOutgoing,
} from "lucide-react";
import { ageFromDate, formatAge, formatDateCreation, isRecent } from "@/crm/anciennete";
import ContactFonctionField from "@/crm/components/ContactFonctionField";
import ContactPicker, { callTargets, needsChoice } from "@/crm/components/ContactPicker";
import BaseModal from "@/crm/components/Modal";
import QualificationPanel from "@/crm/components/QualificationPanel";
import EnrichmentConsole from "@/crm/components/EnrichmentConsole";
import SourceTag, { shortHost } from "@/crm/components/SourceTag";
import GoogleMapsCard from "@/crm/components/GoogleMapsCard";
import { ClipboardCheck } from "lucide-react";

const STATUSES = ["Nouveau", "À qualifier", "À contacter", "Contacté", "En discussion",
  "Rendez-vous prévu", "Proposition envoyée", "Négociation", "Gagné", "Perdu", "À rappeler"];
const CALL_RESULTS = ["Pas de réponse", "Numéro incorrect", "Intéressé", "Pas intéressé", "À rappeler", "Rendez-vous"];
const NEXT_ACTIONS = ["Rappeler", "Envoyer un email", "Planifier un RDV", "Envoyer une proposition", "Aucune"];

const STATUS_STYLE = {
  "Nouveau": "bg-violet-50 text-violet-700", "À qualifier": "bg-slate-100 text-slate-600",
  "À contacter": "bg-sky-50 text-sky-700", "Contacté": "bg-blue-50 text-blue-700",
  "En discussion": "bg-indigo-50 text-indigo-700", "Rendez-vous prévu": "bg-emerald-50 text-emerald-700",
  "Proposition envoyée": "bg-amber-50 text-amber-700", "Négociation": "bg-orange-50 text-orange-700",
  "Gagné": "bg-emerald-50 text-emerald-700", "Perdu": "bg-rose-50 text-rose-700",
  "À rappeler": "bg-amber-50 text-amber-700",
};
const ACT_ICON = { CALL: PhoneCall, EMAIL: Mail, MEETING: Users, NOTE: MessageSquare, STATUS_CHANGE: Clock, ASSIGNMENT: UserPlus, ENRICHMENT: Sparkles, QUALIFICATION: ClipboardCheck };
const ACT_TONE = { CALL: "bg-emerald-50 text-emerald-700", EMAIL: "bg-sky-50 text-sky-700", MEETING: "bg-indigo-50 text-indigo-700", ENRICHMENT: "bg-violet-50 text-violet-700" };

const CARD = "bg-white rounded-2xl border border-slate-200";
const BTN_GHOST = "flex items-center gap-2 px-3.5 py-2 rounded-xl border border-slate-200 text-sm text-slate-600 hover:border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed";
const BTN_PRIMARY = "px-3.5 py-2 rounded-xl bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60";
const FIELD = "w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:border-indigo-500";

// Saisie manuelle d'un numéro : on tolère les écritures courantes (01 23 45 67 89,
// 01.23.45.67.89, +33 1 23 45 67 89) sans imposer de format — c'est le back qui
// normalise au moment de composer (normalize_phone, crm/telephony.py).
const PHONE_CHARS = /^[+(]?[\d\s.\-()]+$/;
const cleanPhone = (v) => (v || "").trim().replace(/\s+/g, " ");
// Validation par le nombre de chiffres plutôt que par la forme : le back retire
// de toute façon les séparateurs (normalize_phone), et E.164 plafonne à 15.
const isPhone = (v) => PHONE_CHARS.test(v) && (v.match(/\d/g) || []).length >= 6
  && (v.match(/\d/g) || []).length <= 15;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
// Boîtes grand public : le domaine d'un gmail.com ne dit rien du site de
// l'entreprise, le proposer comme URL enverrait sur une page d'accueil tierce.
const GENERIC_MAIL_DOMAINS = new Set([
  "gmail.com", "googlemail.com", "hotmail.fr", "hotmail.com", "outlook.fr", "outlook.com",
  "live.fr", "msn.com", "yahoo.fr", "yahoo.com", "orange.fr", "wanadoo.fr", "free.fr",
  "sfr.fr", "neuf.fr", "laposte.net", "bbox.fr", "aol.com", "icloud.com", "me.com",
]);
const domainFromEmail = (email) => ((email || "").split("@")[1] || "").trim().toLowerCase();
const domainFromSite = (site) => (site || "").trim().toLowerCase()
  .replace(/^https?:\/\//, "").replace(/^www\./, "").split(/[/?#]/)[0];
const normalizeSite = (v) => {
  const s = (v || "").trim();
  return !s || /^https?:\/\//i.test(s) ? s : `https://${s}`;
};
const isWebsite = (v) => {
  try { return new URL(normalizeSite(v)).hostname.includes("."); } catch { return false; }
};
// Même règle que departement_from_cp (crm/extraction.py) : 3 chiffres outre-mer,
// 2 ailleurs. Le département pilote la sectorisation, il doit suivre le code postal.
const depFromCp = (cp) => {
  const d = (cp || "").trim();
  if (d.length < 2) return "";
  return ["97", "98"].includes(d.slice(0, 2)) ? d.slice(0, 3) : d.slice(0, 2);
};
/** Corse : 2A et 2B ne se déduisent pas d'un code postal en 20xxx. Le registre
 *  et la Base Adresse Nationale, eux, les distinguent — leur valeur prime sur
 *  un « 20 » calculé, qui ferait sortir la fiche des filtres par département. */
const depForCp = (cp, current) => {
  const dep = depFromCp(cp);
  if (dep === "20" && /^2[AB]$/i.test(current || "")) return current;
  return dep || current || "";
};

/** Adresses plausibles pour l'entreprise : les génériques de son propre domaine
 *  (celui du site, à défaut celui d'un email déjà connu) et les emails de ses
 *  interlocuteurs, souvent la seule piste sur une fiche issue du registre. */
function emailSuggestions(company, current) {
  const domain = domainFromSite(company.site_web)
    || (GENERIC_MAIL_DOMAINS.has(domainFromEmail(company.email)) ? "" : domainFromEmail(company.email));
  const out = domain ? [`contact@${domain}`, `info@${domain}`] : [];
  for (const ct of company.contacts || []) if (ct.email) out.push(ct.email);
  return [...new Set(out)].filter((e) => e && e !== (current || "").trim()).slice(0, 4);
}

/** Le site d'une entreprise porte presque toujours le domaine de son email
 *  professionnel : de quoi renseigner la fiche sans aller chercher l'URL. */
function siteSuggestions(company, current) {
  const d = domainFromEmail(company.email);
  if (!d || GENERIC_MAIL_DOMAINS.has(d)) return [];
  return [`https://${d}`, `https://www.${d}`].filter((s) => s !== normalizeSite(current));
}

export default function CompanyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const tw = useTwilio();
  // Le statut n'est interrogeable que par un admin : on ne le demande pas pour
  // les autres, qui n'ont de toute façon aucune action réservée au Mode Debug.
  const { debugActive } = useDebugMode({ enabled: user?.role === "admin" });
  const [c, setC] = useState(null);
  const [commercials, setCommercials] = useState([]);
  // `?onglet=conversations` : le compte rendu de fin d'appel renvoie
  // directement sur la conversation qui vient d'avoir lieu.
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("onglet") || "activites");
  const [modal, setModal] = useState(null);
  const [picking, setPicking] = useState(false);   // choix du contact avant d'appeler
  const [showMore, setShowMore] = useState(false);
  const [registreLoading, setRegistreLoading] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/companies/${id}`); setC(data); }
    catch { toast.error("Introuvable"); navigate("/entreprises"); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/users/commercials").then(({ data }) => setCommercials(data)).catch(() => {}); }, []);
  // Provenance des contacts (source + lien) : affichée à côté des valeurs,
  // rechargée avec la fiche pour suivre chaque enrichissement.
  const [provenance, setProvenance] = useState({});
  useEffect(() => {
    if (!c?.id) return;
    api.get(`/enrichment/companies/${c.id}/sources`).then(({ data }) => setProvenance(data.provenance || {})).catch(() => {});
  }, [c?.id, c?.telephone, c?.email, c?.site_web]);

  if (!c) return <Loader />;

  const hasMoreInfo = [c.nom_commercial, c.enseigne, c.siren, c.tva, c.categorie_entreprise,
    c.nombre_etablissements, c.convention_collective, c.date_fermeture].some(Boolean) || typeof c.est_siege === "boolean";
  const isManager = user?.role !== "commercial";
  // Édition de la fiche (téléphone) : mêmes rôles que ceux acceptés par
  // PUT /api/companies/{id} côté back, sinon le bouton mènerait à un 403.
  const canEditFiche = user?.role === "admin" || user?.role === "manager";
  const age = ageFromDate(c.date_creation);

  const changeStatus = async (status) => {
    try { await api.patch(`/companies/${id}/status`, { status }); toast.success("Statut mis à jour"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const assign = async (commercial_id) => {
    try { await api.patch(`/companies/${id}/assign`, { commercial_id }); toast.success("Attribué"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  // Réinterroge le registre public : répare une fiche incomplète (ajoutée
  // avant que la création n'aille chercher l'officiel, ou importée sans
  // dirigeants) et remet à jour celles dont l'état civil a bougé.
  const majRegistre = async () => {
    setRegistreLoading(true);
    try {
      const { data } = await api.post(`/companies/${id}/registre`);
      toast.success(`Fiche mise à jour depuis le registre public${
        data.dirigeants?.length ? ` — ${data.dirigeants.length} dirigeant(s)` : ""}`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Registre injoignable"); }
    finally { setRegistreLoading(false); }
  };
  // Étiquette humaine d'un contact (vérifié, faux numéro, messagerie, libre…).
  const verifyContact = async (field, state, label) => {
    try {
      await api.patch(`/companies/${id}/contact-check`, { field, state, label });
      toast.success(state === "unverified" ? "Étiquette retirée" : "Étiquette enregistrée");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Enregistrement impossible"); }
  };
  const enrich = async () => {
    toast.info("Enrichissement en cours…");
    try { await api.post("/enrichment/run", { company_id: id, providers: ["sirene", "maps", "google_business", "website", "email", "phone", "mairie_com", "bottin", "toiletteur_chien_chat", "zouaf", "annuaire_118712"] }); toast.success("Enrichissement terminé"); load(); }
    catch { toast.error("Échec"); }
  };

  const TABS = [
    ["activites", "Activités", Clock, c.activities?.length],
    ["conversations", "Conversations", MessagesSquare, null],
    ["notes", "Notes", MessageSquare, c.notes_count],
    ["reseau", "Cartographie", Network, c.dirigeants?.length],
    ["taches", "Actions", CheckSquare, c.tasks?.length],
    ["historique", "Historique", History, null],
    ["sources", "Sources", Sparkles, null],
  ];

  return (
    <div className="max-w-[1320px] mx-auto">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4">
        <ArrowLeft size={15} /> Retour aux entreprises
      </button>

      <ClientBanner company={c} />
      <ExclusionBanner company={c} />
      {/* Fiche ouverte depuis une campagne d'appel : la consigne et la solution
          à proposer, avant tout le reste. */}
      <CallCampaignBanner companyId={c.id} activeId={params.get("campagne_appel")} />

      {/* EN-TÊTE */}
      <div className={`${CARD} p-5 md:p-6 mb-4`}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex gap-4 min-w-0">
            <div className="h-13 w-13 h-[52px] w-[52px] rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0"><Building2 size={24} /></div>
            <div className="min-w-0">
              <h1 className="font-heading text-2xl font-bold text-slate-900 text-pretty">{c.nom}</h1>
              <div className="flex flex-wrap items-center gap-2 mt-1.5 text-[13px] text-slate-500">
                {c.siret && <span className="font-mono text-xs">{c.siret}</span>}
                {c.siret && c.libelle_naf && <span className="text-slate-300">·</span>}
                {c.libelle_naf && <span>{c.libelle_naf}</span>}
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <span className="inline-flex items-center gap-2 text-xs font-medium px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700">
                  <span className="font-mono">{c.lead_score ?? "—"}</span>{c.score_category}
                </span>
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${STATUS_STYLE[c.status] || "bg-slate-100 text-slate-600"}`}>{c.status}</span>
                {c.client && (
                  <span data-testid="client-badge" className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white">
                    <BadgeCheck size={13} /> Client{(c.client_solutions || []).length > 0 && ` · ${c.client_solutions.map((x) => x.nom).join(", ")}`}
                  </span>
                )}
                {c.commercial_name && (
                  <span className="inline-flex items-center gap-2 text-xs text-slate-500">
                    <span className="h-5 w-5 rounded-full bg-sky-700 text-white flex items-center justify-center text-[10px] font-bold">{c.commercial_name.charAt(0)}</span>
                    {c.commercial_name}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button data-testid="twilio-call-btn" disabled={c.excluded || callTargets(c).length === 0}
              onClick={() => {
                // Plusieurs numéros (standard, contacts) : on choisit qui appeler.
                if (needsChoice(c)) { setPicking(true); return; }
                const [seul] = callTargets(c);
                tw?.makeCall(seul.number, { company_id: c.id, company_name: c.nom, contact_id: seul.contact_id || "" });
              }}
              title={callTargets(c).length ? (needsChoice(c) ? "Choisir qui appeler" : `Appeler ${callTargets(c)[0].number}`) : "Aucun numéro"}
              className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-emerald-700 text-white text-sm font-medium hover:bg-emerald-800 disabled:opacity-40 disabled:cursor-not-allowed">
              <PhoneCall size={15} /> Appeler
            </button>
            <PrepareCallButton company={c} />
            <button data-testid="call-btn" disabled={c.excluded} onClick={() => setModal("call")} className={BTN_GHOST}><Clock size={15} /> Journaliser</button>
            {/* Une seule porte pour la suite à donner : rendez-vous, démo,
                vidéo de démo, email, appel, suivi (voir PlanActionModal). */}
            <button data-testid="plan-btn" onClick={() => setModal("plan")} className={BTN_GHOST}><CalendarPlus size={15} /> Planifier</button>
            <button data-testid="registre-btn" disabled={registreLoading || !(c.siren || c.siret)}
              onClick={majRegistre}
              title="Récupérer l'état civil officiel (dirigeants, SIRET, NAF, effectif) depuis le registre public"
              className={BTN_GHOST}>
              <Landmark size={15} className={registreLoading ? "animate-pulse" : ""} /> Registre
            </button>
            <button data-testid="enrich-btn" disabled={c.excluded} onClick={enrich}
              className="flex items-center gap-2 px-3.5 py-2 rounded-xl border border-indigo-100 bg-indigo-50/60 text-sm font-medium text-indigo-600 hover:bg-indigo-50 disabled:opacity-40">
              <Sparkles size={15} /> Enrichir
            </button>
            <AiEnrichPanel company={c} onDone={load}
              buttonClassName="flex items-center gap-2 px-3.5 py-2 rounded-xl border border-violet-100 bg-violet-50/60 text-sm font-medium text-violet-600 hover:bg-violet-50 disabled:opacity-40" />
            <ClientButton company={c} onSaved={load} />
            {/* Campagne d'appel à partir de cette fiche (admin) : même
                formulaire que depuis la liste, la cible étant cette entreprise. */}
            {user?.role === "admin" && (
              <button data-testid="call-campaign-from-company" onClick={() => setModal("call-campaign")}
                title="Créer une campagne d'appel ciblant cette entreprise" className={BTN_GHOST}>
                <PhoneOutgoing size={15} /> Campagne d'appel
              </button>
            )}
            <ExclusionButton company={c} onSaved={load} />
            {/* Purge des données opérationnelles : admin ET Mode Debug ouvert.
                Le serveur exige déjà les deux (plus le mot de passe) ; l'afficher
                en permanence promettait une action que la fiche ne pouvait pas
                tenir — l'admin ne s'en apercevait qu'après avoir ouvert la
                modale. Le bouton apparaît donc avec la session Debug et
                disparaît avec elle (voir useDebugMode). */}
            {user?.role === "admin" && debugActive && (
              <button data-testid="purge-company-btn" onClick={() => setModal("purge")}
                title="Purger les données opérationnelles de la fiche (Mode Debug actif)"
                className="flex items-center gap-2 px-3.5 py-2 rounded-xl border border-rose-200 text-sm text-rose-600 hover:bg-rose-50">
                <Trash2 size={15} /> Purger
              </button>
            )}
            {/* Corbeille : l'administrateur de PLATEFORME retire la fiche du CRM.
                Rien n'est effacé tout de suite — fiche et données liées partent
                dans la corbeille (30 jours, restaurables), voir pages/Trash.jsx. */}
            {user?.platform_admin && (
              <button data-testid="trash-company-btn" onClick={() => setModal("trash")}
                title="Mettre la fiche à la corbeille (restaurable pendant 30 jours)"
                className="flex items-center gap-2 px-3.5 py-2 rounded-xl border border-rose-200 text-sm text-rose-600 hover:bg-rose-50">
                <Trash2 size={15} /> Supprimer
              </button>
            )}
            {user?.platform_admin && (
              <button data-testid="open-trash-btn" onClick={() => navigate("/corbeille")}
                title="Ouvrir la corbeille des fiches supprimées" className={BTN_GHOST}>
                <Trash2 size={15} /> Corbeille
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2.5 mt-5 pt-4 border-t border-slate-100">
          <select data-testid="status-select" value={c.status} onChange={(e) => changeStatus(e.target.value)}
            className="px-3 py-2 rounded-xl border border-slate-200 text-[13px] bg-white text-slate-600">
            {STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
          {isManager && (
            <select data-testid="assign-select" value={c.commercial_id || ""} onChange={(e) => assign(e.target.value || null)}
              className="px-3 py-2 rounded-xl border border-slate-200 text-[13px] bg-white text-slate-600">
              <option value="">Non attribué</option>
              {commercials.map((cm) => <option key={cm.id} value={cm.id}>{cm.name}</option>)}
            </select>
          )}
          <TagEditor company={c} onSaved={load} />
        </div>
      </div>

      {/* Qualification avancée : verdict motivé, secteur réel, revérification. */}
      <QualificationPanel company={c} onSaved={load} />

      <div className="grid grid-cols-1 lg:grid-cols-[336px_minmax(0,1fr)] gap-4 items-start">
        {/* COLONNE GAUCHE */}
        <div className="space-y-3.5">
          <div className={`${CARD} p-[18px]`}>
            <h3 className="font-heading font-bold text-[15px] text-slate-900 mb-2.5">Informations</h3>
            <PhoneRows company={c} canEdit={canEditFiche} callable={!c.excluded} onSaved={load} provenance={provenance} onVerify={verifyContact}
              onCall={(num) => tw?.makeCall(num, { company_id: c.id, company_name: c.nom })} />
            <EmailRow company={c} canEdit={canEditFiche} onSaved={load} provenance={provenance} onVerify={verifyContact} />
            <WebsiteRow company={c} canEdit={canEditFiche} onSaved={load} provenance={provenance} onVerify={verifyContact} />
            <AddressRow company={c} canEdit={canEditFiche} onSaved={load} />
            <InfoRow label="Effectif" value={c.tranche_effectif || c.effectif} />
            <InfoRow label="Activité" value={[c.naf, c.libelle_naf].filter(Boolean).join(" — ")} />
            <InfoRow label="Secteur" value={c.secteur} />
            {/* La solution à proposer : retenue ici, elle pilote le discours
                d'ouverture et les suggestions du copilote (voir Solutions). */}
            <CompanySolution company={c} onSaved={load} />
            <InfoRow label="Forme" value={c.forme_juridique} />
            <InfoRow label="SIRET" value={c.siret} />
            <InfoRow label="Création" value={formatDateCreation(c.date_creation)}
              badge={age === null ? null : { text: formatAge(age), recent: isRecent(age) }} />

            {showMore && (
              <>
                <InfoRow label="Nom commercial" value={c.nom_commercial || c.enseigne} />
                <InfoRow label="SIREN" value={c.siren} />
                <InfoRow label="N° TVA" value={c.tva} />
                <InfoRow label="Catégorie" value={c.categorie_entreprise} />
                <InfoRow label="Sites" value={c.nombre_etablissements ? `${c.nombre_etablissements_ouverts ?? "?"} ouvert(s) sur ${c.nombre_etablissements}` : null} />
                <InfoRow label="Convention" value={c.convention_collective && `IDCC ${c.convention_collective}`} />
                <InfoRow label="Type de site" value={c.est_siege ? "Siège social" : c.est_siege === false ? "Établissement secondaire" : null} />
                <InfoRow label="Fermeture" value={formatDateCreation(c.date_fermeture)} />
              </>
            )}

            {hasMoreInfo && (
              <button data-testid="toggle-more-info" onClick={() => setShowMore((v) => !v)}
                className="mt-3 flex items-center gap-1.5 text-[12.5px] font-medium text-indigo-600 hover:text-indigo-700">
                <ChevronDown size={14} className={showMore ? "rotate-180 transition-transform" : "transition-transform"} />
                {showMore ? "Masquer les détails" : "Plus d'informations"}
              </button>
            )}
            {/* Fiche Google Maps : lien direct, note et avis — renseignés ici
                ou apportés par l'enrichissement (API Places). */}
            <GoogleMapsCard company={c} onSaved={load} />
          </div>

          <DirigeantsCard company={c} onVoirReseau={() => setTab("reseau")}
            onRegistre={majRegistre} registreLoading={registreLoading} />
          <LabelsCard company={c} />
          <FinancesCard company={c} />
          <ContactsCard company={c} onChanged={load} />
          <SiteCard company={c} onChanged={load} />
        </div>

        {/* COLONNE DROITE : onglets */}
        <div className={`${CARD} overflow-hidden min-w-0`}>
          <div className="flex gap-1 p-2.5 border-b border-slate-100 overflow-x-auto">
            {TABS.map(([k, label, Icon, count]) => {
              const on = tab === k;
              return (
                <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
                  className={`flex items-center gap-2 shrink-0 px-3 py-2 rounded-xl text-[13.5px] font-medium whitespace-nowrap ${on ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                  <Icon size={15} className={on ? "text-white" : "text-slate-400"} /> {label}
                  {count ? <span className={`font-mono text-[10.5px] px-1.5 rounded-full ${on ? "bg-white/15 text-white" : "bg-slate-100 text-slate-500"}`}>{count}</span> : null}
                </button>
              );
            })}
          </div>
          <div className="p-5">
            {tab === "activites" && <Timeline activities={c.activities} />}
            {tab === "conversations" && <CompanyConversations company={c} />}
            {tab === "notes" && <CompanyNotes company={c} onChanged={load} />}
            {tab === "reseau" && <DirigeantNetwork company={c} onChanged={load} />}
            {tab === "taches" && <TasksList tasks={c.tasks} onChanged={load} />}
            {tab === "sources" && <EnrichmentConsole company={c} />}
            {tab === "historique" && (
              <>
                <HistoryList history={c.history} />
                {user?.role === "admin" && (
                  <div className="mt-6 pt-4 border-t border-slate-100">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Activités système (admin)</h4>
                    <SystemActivities companyId={c.id} compact />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {picking && (
        <ContactPicker company={c} onClose={() => setPicking(false)}
          onPick={(t) => { setPicking(false); tw?.makeCall(t.number, { company_id: c.id, company_name: c.nom, contact_id: t.contact_id || "" }); }} />
      )}
      {modal === "call-campaign" && (
        <CallCampaignForm companyIds={[c.id]} onClose={() => setModal(null)}
          onSaved={(data) => { setModal(null); navigate(`/campagnes/appel/${data.id}`); }} />
      )}
      {modal === "call" && <CallModal company={c} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {modal === "purge" && <PurgeCompanyModal company={c} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />}
      {modal === "trash" && (
        <TrashCompanyModal company={c} onClose={() => setModal(null)}
          onDone={() => { setModal(null); navigate("/entreprises"); }} />
      )}
      {modal === "plan" && (
        <PlanActionModal company={c} suggestedEmails={emailSuggestions(c, "")}
          onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      )}
    </div>
  );
}

function InfoRow({ label, value, link, accent, onCall, badge, onEdit, editTestId, source, check, onVerify, field }) {
  return (
    <div className="grid grid-cols-[96px_minmax(0,1fr)] gap-2.5 items-baseline py-2 border-b border-slate-50 last:border-0">
      <span className="text-[12.5px] text-slate-400">{label}</span>
      <div className="flex items-baseline flex-wrap gap-2 min-w-0">
        {value ? (
          link ? (
            // Le domaine suffit à l'écran ; l'URL complète reste dans l'info-bulle.
            <a href={value.startsWith("http") ? value : `https://${value}`} target="_blank" rel="noreferrer" title={value}
              className="inline-flex items-center gap-1 text-[13.5px] text-indigo-600 hover:underline break-words">
              {shortHost(value)} <ExternalLink size={12} />
            </a>
          ) : (
            <span className={`text-[13.5px] break-words ${accent ? "text-indigo-600" : "text-slate-700"}`}>{value}</span>
          )
        ) : <span className="text-[13.5px] text-slate-300">—</span>}
        {badge?.text && (
          <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded-md ${badge.recent ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
            {badge.recent ? "Récente · " : ""}{badge.text}
          </span>
        )}
        {(source || check || onVerify) && <SourceTag value={value} source={source} check={check} onSet={onVerify} field={field} />}
        {((value && onCall) || onEdit) && (
          <span className="ml-auto flex items-center gap-1.5 shrink-0">
            {value && onCall && (
              <button data-testid="inforow-call-btn" onClick={onCall} title={`Appeler ${value}`}
                className="p-1.5 rounded-lg border border-emerald-100 text-emerald-700 hover:bg-emerald-50"><PhoneCall size={13} /></button>
            )}
            {onEdit && (
              <button data-testid={editTestId} onClick={onEdit}
                title={value ? `Modifier — ${label}` : `Ajouter — ${label}`}
                className="flex items-center gap-1 px-1.5 py-1.5 rounded-lg border border-slate-200 text-slate-500 hover:border-indigo-200 hover:text-indigo-600">
                {value ? <Pencil size={13} /> : <><Plus size={13} /><span className="text-[11.5px] font-medium">Ajouter</span></>}
              </button>
            )}
          </span>
        )}
      </div>
    </div>
  );
}

/** Édition en place d'une ligne de la carte Informations.
 *
 * Toutes les lignes passent par le même PUT /companies/{id} : le hook porte
 * l'appel et les états, chaque ligne ne garde que ses champs et sa validation.
 */
function useRowEditor(company, onSaved) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const save = async (payload, message) => {
    setSaving(true);
    try {
      // Chaîne vide et non null : le PUT ignore les champs à null
      // (routers/companies.py), c'est donc "" qui efface une valeur existante.
      await api.put(`/companies/${company.id}`, payload);
      toast.success(message);
      setEditing(false);
      onSaved();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Enregistrement impossible");
    } finally { setSaving(false); }
  };
  return { editing, setEditing, saving, save };
}

function RowEditor({ testId, label, hint, saving, onSave, onCancel, children }) {
  return (
    <div className="py-2.5 border-b border-slate-50" data-testid={`${testId}-editor`}>
      <div className="font-mono text-[10.5px] tracking-[.1em] uppercase text-slate-400 mb-2">{label}</div>
      {children}
      {hint && <p className="text-[11.5px] text-slate-400 mt-2 text-pretty">{hint}</p>}
      <div className="flex gap-2 mt-2.5">
        <button data-testid={`${testId}-save`} onClick={onSave} disabled={saving} className={BTN_PRIMARY}>
          {saving ? "Enregistrement…" : "Enregistrer"}
        </button>
        <button data-testid={`${testId}-cancel`} onClick={onCancel} disabled={saving}
          className="px-3.5 py-2 rounded-xl border border-slate-200 text-sm text-slate-600 hover:border-slate-300 disabled:opacity-60">
          Annuler
        </button>
      </div>
    </div>
  );
}

function Suggestions({ items, onPick, testId }) {
  if (!items.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid={testId}>
      <span className="text-[11.5px] text-slate-400">Suggestions :</span>
      {items.map((v) => (
        <button key={v} data-testid={`${testId}-item`} onClick={() => onPick(v)} title={v}
          className="max-w-full truncate text-[11.5px] px-2 py-1 rounded-lg border border-slate-200 text-slate-600 hover:border-indigo-300 hover:text-indigo-600">
          {v}
        </button>
      ))}
    </div>
  );
}

/** Ligne « Téléphone », éditable par admin/manager.
 *
 * Une fiche issue du registre public arrive souvent sans numéro (l'API
 * recherche-entreprises n'en publie pas) : sans saisie manuelle, la fiche reste
 * inappelable tant que l'enrichissement n'a rien trouvé. Le numéro secondaire
 * n'est pas décoratif — identify_caller (crm/telephony.py) l'interroge aussi,
 * donc un standard renseigné ici fait remonter la fiche au screen-pop entrant.
 */
function PhoneRows({ company, canEdit, callable, onCall, onSaved, provenance = {}, onVerify }) {
  const checks = company.contact_checks || {};
  const { editing, setEditing, saving, save } = useRowEditor(company, onSaved);
  const [form, setForm] = useState({ telephone: "", telephone_secondaire: "" });

  const open = () => {
    setForm({
      telephone: company.telephone || "",
      telephone_secondaire: company.telephone_secondaire || "",
    });
    setEditing(true);
  };

  const submit = () => {
    const principal = cleanPhone(form.telephone);
    const secondaire = cleanPhone(form.telephone_secondaire);
    const invalide = [principal, secondaire].find((n) => n && !isPhone(n));
    if (invalide) return toast.error(`Numéro invalide : ${invalide}`);
    if (principal && principal === secondaire) return toast.error("Les deux numéros sont identiques");
    save({ telephone: principal, telephone_secondaire: secondaire },
      principal || secondaire ? "Téléphone enregistré" : "Téléphone supprimé");
  };

  if (editing) {
    return (
      <RowEditor testId="phone" label="Téléphone" saving={saving} onSave={submit}
        onCancel={() => setEditing(false)}
        hint="Les deux numéros identifient l'entreprise sur un appel entrant. Laisser vide pour effacer.">
        <input data-testid="phone-input" autoFocus placeholder="Principal — 01 23 45 67 89"
          value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })}
          className={`${FIELD} font-mono text-[13px]`} />
        <input data-testid="phone-secondary-input" placeholder="Secondaire (optionnel)"
          value={form.telephone_secondaire} onChange={(e) => setForm({ ...form, telephone_secondaire: e.target.value })}
          className={`${FIELD} font-mono text-[13px] mt-2`} />
      </RowEditor>
    );
  }

  return (
    <>
      <InfoRow label="Téléphone" value={company.telephone} editTestId="edit-phone-btn" source={provenance.telephone}
        check={checks.telephone} field="telephone" onVerify={onVerify ? (st, lb) => onVerify("telephone", st, lb) : null}
        onCall={callable && company.telephone ? () => onCall(company.telephone) : null}
        onEdit={canEdit ? open : null} />
      {company.telephone_secondaire && (
        <InfoRow label="Tél. secondaire" value={company.telephone_secondaire} source={provenance.telephone_secondaire}
          check={checks.telephone_secondaire} field="telephone_secondaire" onVerify={onVerify ? (st, lb) => onVerify("telephone_secondaire", st, lb) : null}
          onCall={callable ? () => onCall(company.telephone_secondaire) : null} />
      )}
    </>
  );
}

function EmailRow({ company, canEdit, onSaved, provenance = {}, onVerify }) {
  // Même logique que le téléphone : une adresse principale, une secondaire,
  // chacune avec sa provenance et son étiquette.
  const { editing, setEditing, saving, save } = useRowEditor(company, onSaved);
  const [form, setForm] = useState({ email: "", email_secondaire: "" });
  const checks = company.contact_checks || {};

  const open = () => {
    setForm({ email: company.email || "", email_secondaire: company.email_secondaire || "" });
    setEditing(true);
  };
  const submit = () => {
    const principal = (form.email || "").trim();
    const secondaire = (form.email_secondaire || "").trim();
    const invalide = [principal, secondaire].find((e) => e && !EMAIL_RE.test(e));
    if (invalide) return toast.error(`Email invalide : ${invalide}`);
    if (principal && principal.toLowerCase() === secondaire.toLowerCase()) return toast.error("Les deux adresses sont identiques");
    save({ email: principal, email_secondaire: secondaire },
      principal || secondaire ? "Email enregistré" : "Email supprimé");
  };

  if (editing) {
    return (
      <RowEditor testId="email" label="Email" saving={saving} onSave={submit}
        onCancel={() => setEditing(false)}
        hint="L'adresse principale sert aux envois d'email et aux workflows. Laisser vide pour effacer.">
        <input data-testid="email-input" autoFocus type="email" placeholder="Principal — contact@entreprise.fr"
          value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
          className={`${FIELD} text-[13px]`} />
        <Suggestions testId="email-suggestions" items={emailSuggestions(company, form.email)} onPick={(v) => setForm({ ...form, email: v })} />
        <input data-testid="email-secondary-input" type="email" placeholder="Secondaire (optionnel)"
          value={form.email_secondaire} onChange={(e) => setForm({ ...form, email_secondaire: e.target.value })}
          className={`${FIELD} text-[13px] mt-2`} />
      </RowEditor>
    );
  }
  return (
    <>
      <InfoRow label="Email" value={company.email} accent editTestId="edit-email-btn" source={provenance.email}
        check={checks.email} field="email" onVerify={onVerify ? (st, lb) => onVerify("email", st, lb) : null}
        onEdit={canEdit ? open : null} />
      {company.email_secondaire && (
        <InfoRow label="Email second." value={company.email_secondaire} accent source={provenance.email_secondaire}
          check={checks.email_secondaire} field="email_secondaire" onVerify={onVerify ? (st, lb) => onVerify("email_secondaire", st, lb) : null} />
      )}
    </>
  );
}

function WebsiteRow({ company, canEdit, onSaved, provenance = {}, onVerify }) {
  const { editing, setEditing, saving, save } = useRowEditor(company, onSaved);
  const [value, setValue] = useState("");

  const submit = () => {
    const raw = (value || "").trim();
    if (raw && !isWebsite(raw)) return toast.error(`Adresse web invalide : ${raw}`);
    // Stocké avec son schéma : l'analyse de site (crm/site_analysis.py) et
    // l'enrichissement requêtent l'URL telle quelle.
    save({ site_web: normalizeSite(raw) }, raw ? "Site web enregistré" : "Site web supprimé");
  };

  if (!editing) {
    return <InfoRow label="Site web" value={company.site_web} link editTestId="edit-site-btn" source={provenance.site_web}
      check={(company.contact_checks || {}).site_web} field="site_web" onVerify={onVerify ? (st, lb) => onVerify("site_web", st, lb) : null}
      onEdit={canEdit ? () => { setValue(company.site_web || ""); setEditing(true); } : null} />;
  }
  return (
    <RowEditor testId="site" label="Site web" saving={saving} onSave={submit}
      onCancel={() => setEditing(false)}
      hint="Le préfixe https:// est ajouté s'il manque.">
      <input data-testid="site-input" autoFocus placeholder="entreprise.fr"
        value={value} onChange={(e) => setValue(e.target.value)}
        className={`${FIELD} text-[13px]`} />
      <Suggestions testId="site-suggestions" items={siteSuggestions(company, value)} onPick={setValue} />
    </RowEditor>
  );
}

function AddressRow({ company, canEdit, onSaved }) {
  const { editing, setEditing, saving, save } = useRowEditor(company, onSaved);
  const [form, setForm] = useState({ adresse: "", code_postal: "", ville: "", departement: "" });

  const open = () => {
    setForm({
      adresse: company.adresse || "", code_postal: company.code_postal || "",
      ville: company.ville || "", departement: company.departement || "",
    });
    setEditing(true);
  };

  const submit = () => {
    const cp = form.code_postal.trim();
    if (cp && !/^\d{5}$/.test(cp)) return toast.error("Code postal : 5 chiffres attendus");
    save({
      adresse: form.adresse.trim(), code_postal: cp, ville: form.ville.trim(),
      // Le département filtre les listes et sert la sectorisation : il suit le
      // code postal saisi, sinon un déménagement laisserait l'ancien en place.
      departement: depForCp(cp, form.departement),
    }, "Adresse enregistrée");
  };

  if (!editing) {
    return <InfoRow label="Adresse" value={formatAdresse(company)} editTestId="edit-address-btn"
      onEdit={canEdit ? open : null} />;
  }
  return (
    <RowEditor testId="address" label="Adresse" saving={saving} onSave={submit}
      onCancel={() => setEditing(false)}
      hint="Choisir une suggestion renseigne aussi code postal, ville et département.">
      {/* Suggestions de la Base Adresse Nationale : une adresse retenue ici est
          géocodable telle quelle par l'enrichissement. */}
      <AddressAutocomplete testId="address-autocomplete" value={form.adresse}
        onChange={(v) => setForm((p) => ({ ...p, adresse: v }))}
        onSelect={(a) => setForm((p) => ({
          ...p, adresse: a.adresse,
          code_postal: a.code_postal || p.code_postal,
          ville: a.ville || p.ville,
          departement: a.departement || p.departement,
        }))}
        inputClassName={`${FIELD} text-[13px]`} />
      <div className="grid grid-cols-[92px_minmax(0,1fr)] gap-2 mt-2">
        <input data-testid="address-cp" placeholder="CP" inputMode="numeric" maxLength={5}
          value={form.code_postal} onChange={(e) => setForm({ ...form, code_postal: e.target.value })}
          className={`${FIELD} font-mono text-[13px]`} />
        <input data-testid="address-ville" placeholder="Ville"
          value={form.ville} onChange={(e) => setForm({ ...form, ville: e.target.value })}
          className={`${FIELD} text-[13px]`} />
      </div>
    </RowEditor>
  );
}

function DirigeantsCard({ company, onVoirReseau, onRegistre, registreLoading }) {
  const [showAll, setShowAll] = useState(false);
  const dirigeants = company.dirigeants || [];
  // Fiche sans dirigeant mais identifiée : le registre public les connaît
  // presque toujours — inutile de laisser la carte disparaître.
  if (!dirigeants.length) {
    if (!(company.siren || company.siret)) return null;
    return (
      <div className={`${CARD} p-[18px]`} data-testid="dirigeants-card-empty">
        <h3 className="font-heading font-bold text-[15px] text-slate-900 mb-2 flex items-center gap-2"><UserCog size={16} /> Dirigeants</h3>
        <p className="text-[13px] text-slate-400">Aucun dirigeant sur cette fiche.</p>
        <button data-testid="dirigeants-registre" onClick={onRegistre} disabled={registreLoading}
          className="mt-3 flex items-center gap-1.5 text-[12.5px] font-medium text-indigo-600 hover:text-indigo-700 disabled:opacity-40">
          <Landmark size={14} className={registreLoading ? "animate-pulse" : ""} />
          {registreLoading ? "Interrogation du registre…" : "Récupérer depuis le registre public"}
        </button>
      </div>
    );
  }
  const visibles = showAll ? dirigeants : dirigeants.slice(0, 3);
  const reste = dirigeants.length - visibles.length;
  const tones = ["bg-indigo-600", "bg-sky-700", "bg-emerald-700", "bg-amber-600", "bg-pink-700", "bg-teal-700"];
  return (
    <div className={`${CARD} p-[18px]`} data-testid="dirigeants-card">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h3 className="font-heading font-bold text-[15px] text-slate-900 flex items-center gap-2"><UserCog size={16} /> Dirigeants</h3>
        <span className="font-mono text-[11.5px] text-slate-400">{dirigeants.length}</span>
      </div>
      <ul className="space-y-3">
        {visibles.map((d, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className={`h-7 w-7 shrink-0 rounded-full text-white flex items-center justify-center text-[11px] font-bold ${tones[i % tones.length]}`}>
              {(d.nom || "?").trim().charAt(0).toUpperCase()}
            </span>
            <div className="min-w-0">
              <div className="text-[13.5px] font-medium text-slate-800 break-words">{d.nom}</div>
              <div className="text-xs text-slate-400 mt-0.5">
                {[d.qualite, d.annee_naissance && `né(e) en ${d.annee_naissance}`, d.type === "morale" && "personne morale"].filter(Boolean).join(" · ") || "—"}
              </div>
            </div>
          </li>
        ))}
      </ul>
      {dirigeants.length > 3 && (
        <button data-testid="toggle-dirigeants" onClick={() => setShowAll((v) => !v)}
          className="mt-3 flex items-center gap-1.5 text-[12.5px] font-medium text-indigo-600 hover:text-indigo-700">
          <ChevronDown size={14} className={showAll ? "rotate-180" : ""} />{showAll ? "Réduire" : `Voir les ${reste} autres`}
        </button>
      )}
      {onVoirReseau && (
        <button data-testid="voir-reseau" onClick={onVoirReseau}
          className="mt-3 flex items-center gap-1.5 text-[12.5px] font-medium text-indigo-600 hover:text-indigo-700">
          <Network size={14} /> Cartographie des sociétés liées
        </button>
      )}
    </div>
  );
}

function LabelsCard({ company }) {
  const labels = company.labels || [];
  if (!labels.length) return null;
  return (
    <div className={`${CARD} p-[18px]`} data-testid="labels-card">
      <h3 className="font-heading font-bold text-[15px] text-slate-900 mb-3 flex items-center gap-2"><Award size={16} /> Labels & certifications</h3>
      <div className="flex flex-wrap gap-2">
        {labels.map((l) => <span key={l} className="text-xs font-medium px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700">{l}</span>)}
      </div>
    </div>
  );
}

function FinancesCard({ company }) {
  const finances = company.finances;
  const annees = finances ? Object.keys(finances).sort().reverse() : [];
  if (!annees.length) return null;
  const fmt = (n) => typeof n === "number"
    ? new Intl.NumberFormat("fr-FR", { notation: "compact", maximumFractionDigits: 1 }).format(n) + " €" : "—";
  return (
    <div className={`${CARD} p-[18px]`} data-testid="finances-card">
      <h3 className="font-heading font-bold text-[15px] text-slate-900 mb-3 flex items-center gap-2"><TrendingUp size={16} /> Finances</h3>
      <div className="space-y-2">
        {annees.map((an) => (
          <div key={an} className="flex items-baseline justify-between gap-3 border-b border-slate-50 last:border-0 pb-2">
            <span className="font-mono text-xs text-slate-400">{an}</span>
            <span className="text-[13px] text-slate-600">
              CA <b className="font-mono font-medium text-slate-900">{fmt(finances[an]?.ca)}</b>
              <span className="text-slate-300 mx-1.5">·</span>
              Rés. <b className={`font-mono font-medium ${(finances[an]?.resultat_net ?? 0) < 0 ? "text-rose-600" : "text-slate-900"}`}>{fmt(finances[an]?.resultat_net)}</b>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function formatAdresse(c) {
  const adresse = (c.adresse || "").trim();
  const parts = adresse ? [adresse] : [];
  const majuscules = adresse.toUpperCase();
  if (c.code_postal && !majuscules.includes(String(c.code_postal))) parts.push(c.code_postal);
  if (c.ville && !majuscules.includes(String(c.ville).toUpperCase())) parts.push(c.ville);
  return parts.join(", ");
}

function Timeline({ activities }) {
  if (!activities?.length) return <p className="text-sm text-slate-400 text-center py-10">Aucune activité enregistrée</p>;
  return (
    <div>
      {activities.map((a) => {
        const Icon = ACT_ICON[a.type] || Clock;
        return (
          <div key={a.id} className="grid grid-cols-[32px_minmax(0,1fr)] gap-3" data-testid="activity-item">
            <div className="flex flex-col items-center">
              <span className={`h-8 w-8 shrink-0 rounded-full flex items-center justify-center ${ACT_TONE[a.type] || "bg-slate-100 text-slate-500"}`}><Icon size={15} /></span>
              <span className="flex-1 w-px bg-slate-100 my-1" />
            </div>
            <div className="pb-4 min-w-0">
              <div className="text-sm font-medium text-slate-900">{a.subject || a.type}</div>
              {a.description && <div className="text-[13.5px] text-slate-500 mt-0.5 text-pretty">{a.description}</div>}
              <div className="flex flex-wrap gap-1.5 mt-2">
                {a.result && <span className="text-[11.5px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{a.result}</span>}
                {a.lead_quality ? <span className="text-[11.5px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">{"★".repeat(a.lead_quality)}</span> : null}
                {a.interest_level && (
                  <span className={`text-[11.5px] font-medium px-2 py-0.5 rounded-full ${a.interest_level === "Chaud" ? "bg-rose-50 text-rose-600" : a.interest_level === "Tiède" ? "bg-amber-50 text-amber-700" : "bg-sky-50 text-sky-700"}`}>{a.interest_level}</span>
                )}
              </div>
              <div className="font-mono text-[11.5px] text-slate-400 mt-2">
                {a.user_name} · {new Date(a.created_at).toLocaleString("fr-FR")}{a.duration ? ` · ${a.duration} min` : ""}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TasksList({ tasks, onChanged }) {
  const done = async (t) => { await api.put(`/tasks/${t.id}`, { status: t.status === "done" ? "pending" : "done" }); onChanged(); };
  const del = async (t) => { await api.delete(`/tasks/${t.id}`); onChanged(); };
  const PRIO = { high: ["Haute", "bg-rose-50 text-rose-600"], medium: ["Moyenne", "bg-amber-50 text-amber-700"], low: ["Basse", "bg-slate-100 text-slate-500"] };
  if (!tasks?.length) return <p className="text-sm text-slate-400 text-center py-10">Aucune action planifiée</p>;
  return (
    <div className="space-y-2.5">
      {tasks.map((t) => {
        const isDone = t.status === "done";
        const [prioLabel, prioClass] = PRIO[t.priority] || PRIO.medium;
        return (
          <div key={t.id} className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 hover:bg-slate-50/60" data-testid="task-item">
            <button onClick={() => done(t)}
              className={`h-[19px] w-[19px] shrink-0 rounded-md border flex items-center justify-center ${isDone ? "bg-indigo-600 border-indigo-600 text-white" : "border-slate-300"}`}>
              {isDone && <CheckSquare size={11} />}
            </button>
            <div className="flex-1 min-w-0">
              <div className={`text-[13.5px] ${isDone ? "line-through text-slate-400" : "text-slate-900"}`}>{t.title}</div>
              <div className="text-xs text-slate-400 mt-0.5">{actionLabel(t.type)} · {t.due_date ? new Date(t.due_date).toLocaleDateString("fr-FR") : "sans date"}</div>
              {t.description && (
                <details className="mt-1">
                  <summary className="cursor-pointer text-[12px] text-indigo-600">Détails</summary>
                  <p className="mt-1 text-[12.5px] text-slate-500 whitespace-pre-line">{t.description}</p>
                </details>
              )}
            </div>
            <span className={`shrink-0 text-[11.5px] font-medium px-2.5 py-1 rounded-full ${prioClass}`}>{prioLabel}</span>
            <button onClick={() => del(t)} className="shrink-0 p-1.5 rounded-lg border border-rose-100 text-rose-600 hover:bg-rose-50"><Trash2 size={14} /></button>
          </div>
        );
      })}
    </div>
  );
}

function HistoryList({ history }) {
  if (!history?.length) return <p className="text-sm text-slate-400 text-center py-10">Aucun historique</p>;
  return (
    <div>
      {history.map((h) => (
        <div key={h.id} className="grid grid-cols-[18px_minmax(0,1fr)] gap-3" data-testid="history-item">
          <div className="flex flex-col items-center">
            <span className="h-2 w-2 rounded-full bg-indigo-500 mt-1.5" />
            <span className="flex-1 w-px bg-slate-100" />
          </div>
          <div className="pb-4">
            <div className="text-[13.5px] text-slate-900 text-pretty">
              {h.action}{h.old_value != null && h.new_value != null ? ` : ${h.old_value || "—"} → ${h.new_value}` : ""}
            </div>
            <div className="font-mono text-[11.5px] text-slate-400 mt-0.5">{h.user_name} · {new Date(h.created_at).toLocaleString("fr-FR")}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ContactsCard({ company, onChanged }) {
  const tw = useTwilio();
  const [adding, setAdding] = useState(false);
  const VIDE = { prenom: "", nom: "", fonction: "", telephone: "", email: "",
                 adresse: "", code_postal: "", ville: "" };
  const [f, setF] = useState(VIDE);
  const add = async () => {
    if (!f.nom) return toast.error("Nom requis");
    await api.post(`/companies/${company.id}/contacts`, f);
    setF(VIDE); setAdding(false); onChanged();
  };
  const del = async (cid) => { await api.delete(`/companies/${company.id}/contacts/${cid}`); onChanged(); };
  return (
    <div className={`${CARD} p-[18px]`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading font-bold text-[15px] text-slate-900">Contacts</h3>
        <button data-testid="add-contact-btn" onClick={() => setAdding((s) => !s)}
          className="p-1.5 rounded-lg border border-slate-200 text-indigo-600 hover:border-slate-300"><Plus size={15} /></button>
      </div>
      {adding && (
        <div className="grid grid-cols-2 gap-2 bg-slate-50/70 border border-slate-100 rounded-xl p-3 mb-3" data-testid="contact-form">
          <input placeholder="Prénom" value={f.prenom} onChange={(e) => setF({ ...f, prenom: e.target.value })} className="px-2.5 py-2 rounded-lg border border-slate-200 text-[13px] outline-none focus:border-indigo-500 min-w-0" />
          <input data-testid="contact-nom" placeholder="Nom *" value={f.nom} onChange={(e) => setF({ ...f, nom: e.target.value })} className="px-2.5 py-2 rounded-lg border border-slate-200 text-[13px] outline-none focus:border-indigo-500 min-w-0" />
          {/* Fonction : liste de suggestions, saisie libre conservée — les
              intitulés du terrain débordent toujours d'une liste fermée. */}
          <ContactFonctionField className="col-span-2" value={f.fonction} onChange={(v) => setF({ ...f, fonction: v })} />
          <input placeholder="Téléphone" value={f.telephone} onChange={(e) => setF({ ...f, telephone: e.target.value })} className="col-span-2 px-2.5 py-2 rounded-lg border border-slate-200 font-mono text-[13px] outline-none focus:border-indigo-500" />
          <input placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="col-span-2 px-2.5 py-2 rounded-lg border border-slate-200 text-[13px] outline-none focus:border-indigo-500" />
          {/* Adresse du contact (agence, domicile) : suggérée par la Base
              Adresse Nationale, code postal et ville suivent la sélection. */}
          <AddressAutocomplete
            testId="contact-adresse" className="col-span-2" value={f.adresse}
            placeholder={`Adresse${company.ville ? ` (par défaut : ${company.ville})` : ""}`}
            onChange={(v) => setF((p) => ({ ...p, adresse: v }))}
            onSelect={(a) => setF((p) => ({
              ...p, adresse: a.adresse,
              code_postal: a.code_postal || p.code_postal,
              ville: a.ville || p.ville,
            }))}
            inputClassName="w-full px-2.5 py-2 rounded-lg border border-slate-200 text-[13px] outline-none focus:border-indigo-500" />
          <button data-testid="save-contact" onClick={add} className="col-span-2 py-2 rounded-lg bg-indigo-600 text-white text-[13px] font-medium hover:bg-indigo-700">Ajouter le contact</button>
        </div>
      )}
      {company.contacts?.length ? company.contacts.map((ct) => (
        <div key={ct.id} className="flex items-center gap-2.5 py-2.5 border-b border-slate-50 last:border-0">
          <div className="flex-1 min-w-0">
            <div className="text-[13.5px] font-medium text-slate-900 truncate">{ct.prenom} {ct.nom}</div>
            <div className="text-xs text-slate-400 mt-0.5 truncate">{ct.fonction}{ct.telephone && ` · ${ct.telephone}`}</div>
          </div>
          {ct.telephone && !company.excluded && (
            <button data-testid={`contact-call-btn-${ct.id}`} title={`Appeler ${ct.prenom} ${ct.nom}`}
              onClick={() => tw?.makeCall(ct.telephone, { company_id: company.id, company_name: company.nom, contact_id: ct.id })}
              className="p-1.5 rounded-lg border border-emerald-100 text-emerald-700 hover:bg-emerald-50"><PhoneCall size={14} /></button>
          )}
          <button onClick={() => del(ct.id)} className="p-1.5 rounded-lg border border-rose-100 text-rose-600 hover:bg-rose-50"><Trash2 size={14} /></button>
        </div>
      )) : !adding && <p className="text-sm text-slate-400">Aucun contact</p>}
    </div>
  );
}

function TagEditor({ company, onSaved }) {
  const [tags, setTags] = useState(company.tags || []);
  const [input, setInput] = useState("");
  const save = async (next) => { setTags(next); await api.patch(`/companies/${company.id}/tags`, { tags: next }); onSaved(); };
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="tag-editor">
      {tags.map((t) => (
        <span key={t} className="inline-flex items-center gap-1.5 bg-amber-50 text-amber-700 rounded-lg pl-2.5 pr-2 py-1 text-xs font-medium">
          {t}<button onClick={() => save(tags.filter((x) => x !== t))} className="text-amber-500 hover:text-amber-800"><X size={11} /></button>
        </span>
      ))}
      <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="+ tag" data-testid="tag-input"
        onKeyDown={(e) => { if (e.key === "Enter" && input.trim()) { save([...tags, input.trim().toUpperCase()]); setInput(""); } }}
        className="w-20 px-2.5 py-1.5 rounded-lg border border-dashed border-slate-300 text-xs outline-none focus:border-solid focus:border-indigo-500" />
    </div>
  );
}

function Modal({ title, children, onClose, wide }) {
  return (
    <BaseModal onClose={onClose} label={title} className={wide ? "max-w-[480px]" : "max-w-[420px]"}>
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-heading font-bold text-[17px] text-slate-900">{title}</h3>
        <button onClick={onClose} aria-label="Fermer" className="text-slate-400 hover:text-slate-900"><X size={18} /></button>
      </div>
      <div className="mt-3.5">{children}</div>
    </BaseModal>
  );
}

function CallModal({ company, onClose, onSaved }) {
  const [result, setResult] = useState("");
  const [duration, setDuration] = useState("");
  const [description, setDescription] = useState("");
  const [nextDate, setNextDate] = useState("");
  const [noContact, setNoContact] = useState(false);
  const [leadQuality, setLeadQuality] = useState(0);
  const [interest, setInterest] = useState("");
  const [nextAction, setNextAction] = useState("");
  const INTEREST_ON = { Froid: "bg-sky-50 text-sky-700 border-sky-200", Tiède: "bg-amber-50 text-amber-700 border-amber-200", Chaud: "bg-rose-50 text-rose-600 border-rose-200" };
  const save = async () => {
    if (!result) return toast.error("Sélectionnez un résultat");
    await api.post("/activities/call", {
      company_id: company.id, type: "CALL", subject: `Appel — ${result}`, description, result,
      duration: duration ? Number(duration) : null,
      lead_quality: leadQuality || null,
      interest_level: interest || null,
      next_action: nextAction && nextAction !== "Aucune" ? nextAction : (result === "À rappeler" ? "Rappeler" : null),
      next_action_date: nextDate || null,
    });
    if (noContact) {
      try {
        await api.post(`/companies/${company.id}/exclusions`, {
          type: "COMPANY", reason: "Demande explicite du prospect", blocked_channels: ["ALL"],
          notes: description || "Signalé lors d'un appel",
        });
        toast.success("Appel enregistré · opposition créée");
      } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    } else { toast.success("Appel enregistré"); }
    onSaved();
  };
  const LABEL = "font-mono text-[10.5px] tracking-[.1em] uppercase text-slate-400 mb-2";
  return (
    <Modal title="Enregistrer un appel" onClose={onClose} wide>
      <div data-testid="call-modal">
        {company.telephone && <div className="bg-indigo-50/70 text-indigo-700 rounded-xl px-3 py-2.5 font-mono text-sm">{company.telephone}</div>}

        <div className="mt-4">
          <div className={LABEL}>Résultat</div>
          <div className="grid grid-cols-2 gap-2">
            {CALL_RESULTS.map((r) => (
              <button key={r} data-testid={`call-result-${r}`} onClick={() => setResult(r)}
                className={`text-[12.5px] font-medium rounded-xl py-2.5 border ${result === r ? "border-indigo-500 bg-indigo-50/60 text-indigo-700" : "border-slate-200 text-slate-600"}`}>{r}</button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-4">
          <div>
            <div className={LABEL}>Qualité du lead</div>
            <div className="flex gap-1" data-testid="lead-quality-stars">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} data-testid={`star-${n}`} onClick={() => setLeadQuality(n === leadQuality ? 0 : n)}
                  className={`text-xl leading-none ${n <= leadQuality ? "text-amber-400" : "text-slate-200"}`}>★</button>
              ))}
            </div>
          </div>
          <div>
            <div className={LABEL}>Intérêt</div>
            <div className="flex flex-wrap gap-1.5">
              {["Froid", "Tiède", "Chaud"].map((lvl) => (
                <button key={lvl} data-testid={`interest-${lvl}`} onClick={() => setInterest(interest === lvl ? "" : lvl)}
                  className={`text-[12.5px] font-medium rounded-lg px-2.5 py-1.5 border ${interest === lvl ? INTEREST_ON[lvl] : "border-slate-200 text-slate-500"}`}>{lvl}</button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 mt-4">
          <input placeholder="Durée (min)" type="number" value={duration} onChange={(e) => setDuration(e.target.value)} className={FIELD} />
          <select data-testid="next-action-select" value={nextAction} onChange={(e) => setNextAction(e.target.value)} className={`${FIELD} bg-white text-slate-600`}>
            <option value="">Prochaine action —</option>
            {NEXT_ACTIONS.map((a) => <option key={a}>{a}</option>)}
          </select>
        </div>

        <textarea placeholder="Notes de l'appel…" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className={`${FIELD} mt-2 resize-y`} />

        {result === "À rappeler" && (
          <input type="date" value={nextDate} onChange={(e) => setNextDate(e.target.value)} className={`${FIELD} mt-2 text-slate-600`} />
        )}

        <label className="flex items-center gap-2.5 mt-2.5 rounded-xl border border-rose-100 bg-rose-50 px-3 py-2.5 cursor-pointer">
          <input type="checkbox" data-testid="call-no-contact" checked={noContact} onChange={(e) => setNoContact(e.target.checked)} className="accent-rose-600" />
          <span className="text-[13px] text-rose-700">Ne souhaite plus être contacté — créer une opposition</span>
        </label>

        <div className="flex gap-2 mt-4">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-600 hover:border-slate-300">Annuler</button>
          <button data-testid="save-call" onClick={save} className={`flex-1 py-2.5 ${BTN_PRIMARY}`}>Enregistrer</button>
        </div>
      </div>
    </Modal>
  );
}
